import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Card, Text, Button, ActivityIndicator, Modal, Portal, TextInput, Chip } from 'react-native-paper';
import { colors, spacing, typography } from '../../constants/theme';
import { adminService } from '../../services/adminService';

interface VendorsScreenProps {
  onBack?: () => void;
}

interface VendorType {
  id: number;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
}

interface Vendor {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  vendorTypes?: VendorType[];
  reviewTemplates?: any[];
}

interface VendorFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  vendorTypeIds: number[];
}

interface ReviewTemplate {
  id: number;
  name: string;
  description?: string;
  templateType: string;
  version: number;
  isActive: boolean;
}

export const VendorsScreen: React.FC<VendorsScreenProps> = ({ onBack }) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendorTypes, setVendorTypes] = useState<VendorType[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    vendorTypeIds: [],
  });
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedVendorForTemplates, setSelectedVendorForTemplates] = useState<Vendor | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<ReviewTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    loadVendors();
    loadVendorTypes();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const vendorsData = await adminService.listVendors<Vendor>();
      setVendors(vendorsData || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
      Alert.alert('Error', 'Failed to load vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVendorTypes = async () => {
    try {
      const vendorTypesData = await adminService.listVendorTypes<VendorType>();
      setVendorTypes(vendorTypesData || []);
    } catch (error) {
      console.error('Error loading vendor types:', error);
      setVendorTypes([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      description: '',
      vendorTypeIds: [],
    });
    setEditingVendor(null);
  };

  const handleCreateVendor = () => {
    resetForm();
    setModalVisible(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      description: vendor.description || '',
      vendorTypeIds: vendor.vendorTypes?.map(vt => vt.id) || [],
    });
    setModalVisible(true);
  };

  const handleSaveVendor = async () => {
    if (!formData.name.trim()) {
      Alert.alert('Error', 'Vendor name is required');
      return;
    }

    try {
      setSubmitting(true);
      
      const vendorData = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        description: formData.description.trim() || null,
        vendorTypeIds: formData.vendorTypeIds,
      };

      if (editingVendor) {
        await adminService.updateVendor(editingVendor.id, vendorData);
        Alert.alert('Success', 'Vendor updated successfully');
      } else {
        await adminService.createVendor(vendorData);
        Alert.alert('Success', 'Vendor created successfully');
      }

      setModalVisible(false);
      resetForm();
      await loadVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      Alert.alert('Error', 'Failed to save vendor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const toggleVendorType = (vendorTypeId: number) => {
    setFormData(prev => ({
      ...prev,
      vendorTypeIds: prev.vendorTypeIds.includes(vendorTypeId)
        ? prev.vendorTypeIds.filter(id => id !== vendorTypeId)
        : [...prev.vendorTypeIds, vendorTypeId]
    }));
  };

  const handleToggleVendorStatus = async (vendor: Vendor) => {
    const action = vendor.isActive ? 'deactivate' : 'activate';
    Alert.alert(
      `${action === 'deactivate' ? 'Deactivate' : 'Activate'} Vendor`,
      `Are you sure you want to ${action} ${vendor.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: action === 'deactivate' ? 'Deactivate' : 'Activate',
          style: action === 'deactivate' ? 'destructive' : 'default',
          onPress: async () => {
            try {
              await adminService.updateVendor(vendor.id, {
                isActive: !vendor.isActive
              });
              await loadVendors(); // Refresh the list
              Alert.alert('Success', `Vendor ${action}d successfully`);
            } catch (error) {
              console.error(`Error ${action}ing vendor:`, error);
              Alert.alert('Error', `Failed to ${action} vendor`);
            }
          }
        }
      ]
    );
  };

  const handleManageTemplates = async (vendor: Vendor) => {
    setSelectedVendorForTemplates(vendor);
    setLoadingTemplates(true);
    setTemplateModalVisible(true);
    
    try {
      // Load all templates and vendor's templates
      const [allTemplatesData, vendorTemplatesData] = await Promise.all([
        adminService.listAdminTemplates<ReviewTemplate>(),
        adminService.listVendorTemplates<ReviewTemplate>(vendor.id)
      ]);
      
      setAvailableTemplates(allTemplatesData || []);
      
      // Update the vendor with its templates
      const updatedVendor = { ...vendor, reviewTemplates: vendorTemplatesData || [] };
      setSelectedVendorForTemplates(updatedVendor);
    } catch (error) {
      console.error('Error loading templates:', error);
      Alert.alert('Error', 'Failed to load templates');
      setAvailableTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleToggleTemplateAssociation = async (templateId: number, isAssociated: boolean) => {
    if (!selectedVendorForTemplates) return;
    
    try {
      if (isAssociated) {
        // Remove association
        await adminService.removeTemplateFromVendor(selectedVendorForTemplates.id, templateId);
      } else {
        // Add association
        await adminService.addTemplateToVendor(selectedVendorForTemplates.id, templateId, {
          isPrimary: false,
          notes: ''
        });
      }
      
      // Refresh vendor data
      await loadVendors();
      
      // Update the selected vendor in the modal with fresh data
      const vendorTemplatesData = await adminService.listVendorTemplates<ReviewTemplate>(selectedVendorForTemplates.id);
      const updatedVendor = { ...selectedVendorForTemplates, reviewTemplates: vendorTemplatesData || [] };
      setSelectedVendorForTemplates(updatedVendor);
    } catch (error) {
      console.error('Error updating template association:', error);
      Alert.alert('Error', 'Failed to update template association');
    }
  };

  const renderVendorCard = (vendor: Vendor) => (
    <Card key={vendor.id} style={[styles.card, !vendor.isActive && styles.inactiveCard]}>
      <Card.Content>
        <View style={styles.vendorHeader}>
          <View style={styles.vendorInfo}>
            <Text style={styles.vendorName}>{vendor.name}</Text>
            {vendor.vendorTypes && vendor.vendorTypes.length > 0 && (
              <Text style={styles.vendorCategory}>
                {vendor.vendorTypes.map(type => type.name).join(', ')}
              </Text>
            )}
            {vendor.email && (
              <Text style={styles.vendorDetail}>📧 {vendor.email}</Text>
            )}
            {vendor.phone && (
              <Text style={styles.vendorDetail}>📞 {vendor.phone}</Text>
            )}
            {vendor.address && (
              <Text style={styles.vendorDetail}>📍 {vendor.address}</Text>
            )}
          </View>
          <View style={styles.vendorActions}>
            <Button
              mode="outlined"
              onPress={() => handleEditVendor(vendor)}
              style={styles.editButton}
              compact
            >
              Edit
            </Button>
            <Button
              mode="outlined"
              onPress={() => handleManageTemplates(vendor)}
              style={styles.templatesButton}
              compact
            >
              Templates
            </Button>
            <Button
              mode="outlined"
              onPress={() => handleToggleVendorStatus(vendor)}
              style={vendor.isActive ? styles.deactivateButton : styles.activateButton}
              compact
            >
              {vendor.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </View>
        </View>
        
        <View style={styles.vendorStats}>
          <Text style={styles.stat}>
            Status: {vendor.isActive ? 'Active' : 'Inactive'}
          </Text>
          <Text style={styles.stat}>
            Added: {new Date(vendor.createdAt).toLocaleDateString()}
          </Text>
        </View>
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View testID="admin-vendors-screen" style={styles.loading}>
        <ActivityIndicator size="large" />
        <Text style={styles.loadingText}>Loading vendors...</Text>
      </View>
    );
  }

  return (
    <View testID="admin-vendors-screen" style={styles.container}>
      <ScrollView testID="admin-vendors-scroll" style={styles.scrollView}>
        <Card style={styles.headerCard}>
          <Card.Content>
            {onBack && (
              <View style={styles.backButtonContainer}>
                <Button
                  testID="admin-back-button"
                  mode="text"
                  onPress={onBack}
                  style={styles.backButton}
                  icon={() => <Text style={styles.backIcon}>←</Text>}
                >
                  Back to Admin Menu
                </Button>
              </View>
            )}
            <Text style={styles.title}>Vendor Management</Text>
            <Text style={styles.subtitle}>Manage vendor information and relationships</Text>
          </Card.Content>
        </Card>

        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Vendors ({vendors.length})</Text>
          <Button
            testID="admin-create-vendor-button"
            mode="contained"
            onPress={handleCreateVendor}
            style={styles.createButton}
          >
            + New Vendor
          </Button>
        </View>
        
        {vendors.length > 0 ? (
          vendors.map(renderVendorCard)
        ) : (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <Text style={styles.emptyText}>No vendors found</Text>
              <Text style={styles.emptySubtext}>Add vendors to manage your network of service providers</Text>
            </Card.Content>
          </Card>
        )}
      </ScrollView>
      
      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={handleCancelModal}
          contentContainerStyle={styles.modalContainer}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </Text>
            
            <ScrollView style={styles.modalScroll}>
              <TextInput
                label="Vendor Name *"
                value={formData.name}
                onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                style={styles.input}
                mode="outlined"
              />
              
              <TextInput
                label="Email"
                value={formData.email}
                onChangeText={(text) => setFormData(prev => ({ ...prev, email: text }))}
                style={styles.input}
                mode="outlined"
                keyboardType="email-address"
                autoCapitalize="none"
              />
              
              <TextInput
                label="Phone"
                value={formData.phone}
                onChangeText={(text) => setFormData(prev => ({ ...prev, phone: text }))}
                style={styles.input}
                mode="outlined"
                keyboardType="phone-pad"
              />
              
              <TextInput
                label="Address"
                value={formData.address}
                onChangeText={(text) => setFormData(prev => ({ ...prev, address: text }))}
                style={styles.input}
                mode="outlined"
                multiline
                numberOfLines={2}
              />
              
              <TextInput
                label="Description"
                value={formData.description}
                onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
                style={styles.input}
                mode="outlined"
                multiline
                numberOfLines={3}
              />
              
              <Text style={styles.fieldLabel}>Vendor Types</Text>
              <View style={styles.vendorTypesContainer}>
                {vendorTypes.map((vendorType) => (
                  <Chip
                    key={vendorType.id}
                    selected={formData.vendorTypeIds.includes(vendorType.id)}
                    onPress={() => toggleVendorType(vendorType.id)}
                    style={[styles.vendorTypeChip, formData.vendorTypeIds.includes(vendorType.id) && styles.selectedChip]}
                  >
                    {vendorType.name}
                  </Chip>
                ))}
              </View>
            </ScrollView>
            
            <View style={styles.modalActions}>
              <Button
                mode="outlined"
                onPress={handleCancelModal}
                style={styles.cancelButton}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={handleSaveVendor}
                style={styles.saveButton}
                loading={submitting}
                disabled={submitting}
              >
                {editingVendor ? 'Update' : 'Create'}
              </Button>
            </View>
          </View>
        </Modal>
      </Portal>
      
      <Portal>
        <Modal
          visible={templateModalVisible}
          onDismiss={() => {
            setTemplateModalVisible(false);
            setSelectedVendorForTemplates(null);
            setAvailableTemplates([]);
          }}
          contentContainerStyle={styles.modalContainer}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              Manage Templates for {selectedVendorForTemplates?.name}
            </Text>
            
            <ScrollView style={styles.modalScroll}>
              {loadingTemplates ? (
                <View style={styles.loading}>
                  <ActivityIndicator size="large" />
                  <Text style={styles.loadingText}>Loading templates...</Text>
                </View>
              ) : (
                <>
                  {availableTemplates.length === 0 ? (
                    <Text style={styles.emptyTemplates}>
                      No templates available. Create templates first in the Templates section.
                    </Text>
                  ) : (
                    availableTemplates
                      .filter(template => template.isActive)
                      .map(template => {
                        const isAssociated = selectedVendorForTemplates?.reviewTemplates?.some(
                          t => t.id === template.id
                        ) || false;
                        
                        return (
                          <Card key={template.id} style={[
                            styles.templateCard,
                            isAssociated && styles.associatedTemplateCard
                          ]}>
                            <Card.Content>
                              <View style={styles.templateCardHeader}>
                                <View style={styles.templateCardInfo}>
                                  <Text style={styles.templateCardName}>{template.name}</Text>
                                  <Text style={styles.templateCardType}>Type: {template.templateType}</Text>
                                  <Text style={styles.templateCardVersion}>Version: {template.version}</Text>
                                </View>
                                <Button
                                  mode={isAssociated ? 'contained' : 'outlined'}
                                  onPress={() => handleToggleTemplateAssociation(template.id, isAssociated)}
                                  style={styles.templateToggleButton}
                                  compact
                                >
                                  {isAssociated ? 'Remove' : 'Add'}
                                </Button>
                              </View>
                            </Card.Content>
                          </Card>
                        );
                      })
                  )}
                </>
              )}
            </ScrollView>
            
            <Button
              mode="outlined"
              onPress={() => {
                setTemplateModalVisible(false);
                setSelectedVendorForTemplates(null);
                setAvailableTemplates([]);
              }}
              style={styles.modalCloseButton}
            >
              Close
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
    padding: spacing.md,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    ...typography.body1,
    marginTop: spacing.md,
    color: colors.textSecondary,
  },
  headerCard: {
    marginBottom: spacing.lg,
  },
  backButtonContainer: {
    marginBottom: spacing.md,
  },
  backButton: {
    alignSelf: 'flex-start',
    marginLeft: -spacing.sm,
  },
  backIcon: {
    fontSize: 18,
    color: colors.primary,
  },
  title: {
    ...typography.h1,
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typography.body1,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typography.h2,
  },
  createButton: {
    // Custom styling for create button
  },
  card: {
    marginBottom: spacing.md,
  },
  inactiveCard: {
    backgroundColor: colors.surface,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
    opacity: 0.7,
  },
  vendorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  vendorInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  vendorName: {
    ...typography.h3,
    marginBottom: spacing.xs,
  },
  vendorCategory: {
    ...typography.caption,
    color: colors.primary,
    marginBottom: spacing.xs,
    fontWeight: '600',
  },
  vendorDetail: {
    ...typography.body1,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  vendorActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  editButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  templatesButton: {
    borderColor: colors.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deactivateButton: {
    borderColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activateButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vendorStats: {
    flexDirection: 'row',
    marginTop: spacing.md,
    gap: spacing.md,
  },
  stat: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  emptyCard: {
    marginTop: spacing.xl,
  },
  emptyText: {
    ...typography.h3,
    textAlign: 'center',
    marginBottom: spacing.sm,
    color: colors.textSecondary,
  },
  emptySubtext: {
    ...typography.body1,
    textAlign: 'center',
    color: colors.textSecondary,
  },
  modalContainer: {
    margin: spacing.lg,
    maxHeight: '90%',
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: spacing.lg,
    maxHeight: '100%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  modalScroll: {
    maxHeight: '70%',
  },
  input: {
    marginBottom: spacing.md,
  },
  fieldLabel: {
    ...typography.body1,
    fontWeight: '600',
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
  },
  vendorTypesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  vendorTypeChip: {
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  selectedChip: {
    backgroundColor: colors.primary,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
  },
  emptyTemplates: {
    ...typography.body1,
    textAlign: 'center',
    marginVertical: spacing.xl,
    color: colors.textSecondary,
  },
  templateCard: {
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  associatedTemplateCard: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  templateCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  templateCardInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  templateCardName: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  templateCardType: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  templateCardVersion: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  templateToggleButton: {
    minWidth: 80,
  },
  modalCloseButton: {
    marginTop: spacing.lg,
  },
});

export default VendorsScreen;
